import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiService} from '../api.service';
import {OfferCatalog, ShortOfferCatalog} from "@core/shared/models/offer/offer-catalog";
import {map} from "rxjs/operators";
import {plainToClass} from "class-transformer";
import {Offer} from "@core/shared/models/offer";
import {Pagination} from "@core/shared/models/pagination";
import {Channel} from "@core/shared/models/channel";
import {AdditionalHeader} from "@core/shared/models/http-client";
import {AllPaginateItemsAPI} from "@core/shared/models/all-paginate-items-api";
import {ShortItem} from "@core/shared/models/short-item";

@Injectable({
    providedIn: 'root'
})
export class OfferCatalogService {

    public selfItems: BehaviorSubject<OfferCatalog[]|ShortOfferCatalog[]> = new BehaviorSubject([]);

    constructor(
        private _apiService: ApiService,
        private _httpClient: HttpClient,
    ) {
    }

    public getItemsAPI(params?: string[]): Observable<OfferCatalog[]> {

        return new AllPaginateItemsAPI<OfferCatalog>(this.getPaginationItemsAPI.bind(this), params).request$;
    }

    public getShortItemsAPI(params?: string[]): Observable<ShortOfferCatalog[]> {

        return new AllPaginateItemsAPI<ShortOfferCatalog>(this.getPaginationShortItemsAPI.bind(this), params).request$;
    }

    public getPaginationItemsAPI(params?: string[], additionalHeaders?: AdditionalHeader[]): Observable<Pagination<OfferCatalog>> {

        let headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        additionalHeaders?.forEach(header => headers = headers.append(header.name, header.value.toString()));

        let url: string = `${this._apiService.getApiUrl(false, true)}/offers/catalog`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<OfferCatalog>>(url, {
            headers: headers
        }).pipe(
            map((pagination: Pagination<OfferCatalog>): Pagination<OfferCatalog> => {

                return Object.assign(pagination, {
                    items: pagination.items.map((item: OfferCatalog): OfferCatalog => {

                        return Object.assign(item, {
                            offer: plainToClass(Offer, item.offer)
                        });
                    })
                });
            })
        );
    }

    public getPaginationShortItemsAPI(params?: string[], additionalHeaders?: AdditionalHeader[]): Observable<Pagination<ShortOfferCatalog>> {

        let headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        additionalHeaders?.forEach(header => headers = headers.append(header.name, header.value.toString()));

        let url: string = `${this._apiService.getApiUrl(false, true)}/offers/catalog/shorts`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<ShortItem<OfferCatalog>>>(url, {
            headers: headers
        }).pipe(
            map((pagination: Pagination<ShortItem<OfferCatalog>>): Pagination<ShortOfferCatalog> => {

                return Object.assign(pagination, {
                    items: pagination.items.map((item: ShortItem<OfferCatalog>): ShortOfferCatalog => {

                        return Object.assign(item.shortValues, {
                            id: parseInt(item.shortIdentifier),
                            offer: plainToClass(Offer, item.shortValues.offer)
                        }) as ShortOfferCatalog;
                    })
                });
            })
        );
    }

    public getItemsRequestAPI(params?: string[]): Observable<OfferCatalog[]> {

        return new AllPaginateItemsAPI<OfferCatalog>(this.getPaginationItemsRequestAPI.bind(this), params).request$;
    }

    public getChannelsRequestAPI(offer, params?: string[]): Observable<Channel[]> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/offers/${offer}/channels`;

        params = params || [];

        params.push('disablePagination=1');

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Channel[]>(url, {
            headers: headers
        });
    }

    public getPaginationItemsRequestAPI(params?: string[]): Observable<Pagination<OfferCatalog>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/offers/catalog/request`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<OfferCatalog>>(url, {
            headers: headers
        }).pipe(
            map((pagination: Pagination<OfferCatalog>): Pagination<OfferCatalog> => {

                return Object.assign(pagination, {
                    items: pagination.items.map((item: OfferCatalog): OfferCatalog => {

                        return Object.assign(item, {
                            offer: plainToClass(Offer, item.offer)
                        });
                    })
                });
            })
        );
    }

    public getItemRequestAPI(id: number): Observable<OfferCatalog> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        const url: string = `${this._apiService.getApiUrl(false, true)}/offers/catalog/request/${id}`;

        return this._httpClient.get<OfferCatalog>(url, {
            headers: headers
        }).pipe(
            map((item: OfferCatalog): OfferCatalog => {

                return Object.assign(item, {
                    offer: plainToClass(Offer, item.offer)
                });
            })
        );
    }

    public updateItemRequestAPI(id: number, data: object): Observable<OfferCatalog> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        const url: string = `${this._apiService.getApiUrl(false, true)}/offers/catalog/request/${id}`;

        return this._httpClient.patch<OfferCatalog>(url, data, {
            headers: headers
        }).pipe(
            map((item: OfferCatalog): OfferCatalog => {

                return Object.assign(item, {
                    offer: plainToClass(Offer, item.offer)
                });
            })
        );
    }

    public createItemAPI(data: object): Observable<any> {

        const url = `${this._apiService.getApiUrl(false, true)}/offers/catalog`;

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<any>(url, data, {
            headers: headers
        });
    }

    public createItemsAPI(data: object): Observable<any> {

        const url = `${this._apiService.getApiUrl(false, true)}/offers/catalog/multiple`;

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<any>(url, data, {
            headers: headers
        });
    }


    public updateItemsAPI(data: object): Observable<Offer[]> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.patch<Offer[]>(`${this._apiService.getApiUrl(false, true)}/offers/catalog/multiple`, data, {
            headers: headers
        });
    }

    public deleteItemAPI(id: number): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.delete<void>(`${this._apiService.getApiUrl(false, true)}/offers/catalog/${id}`,{
            headers: headers
        });
    }

    public getSelfItemByOffer(offer: Offer): OfferCatalog|ShortOfferCatalog {

        return this.selfItems.getValue().find((item: OfferCatalog): boolean => {

            return item.offer.id === offer.id;
        });
    }

    public addOfferToSelfItems(offerCatalog: OfferCatalog): void {

        const items: OfferCatalog[]|ShortOfferCatalog[] = this.selfItems.getValue();

        items.push(offerCatalog);

        this.selfItems.next(items);
    }

    public removeToSelfItems(offerCatalog: OfferCatalog): void {

        const items: OfferCatalog[]|ShortOfferCatalog[] = this.selfItems.getValue();

        const index: number = items.findIndex(item => item.id === offerCatalog.id);

        items.splice(index, 1);

        this.selfItems.next(items);
    }

    public exportItemsRequest(): Observable<Blob> {

        return this._httpClient.get(`${this._apiService.getApiUrl(false, true)}/offers/catalog/request/export`, {
            responseType: 'blob'
        });

    }
}
